import { Skeleton, Stack } from '@mui/material';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { AnalyticalDatasetHeader } from '../../components/AnalyticalDatasetHeader';
import { EmptyStateCard } from '../../components/EmptyStateCard';
import { TableDataPreview } from '../../components/TableDataPreview';
import { TableSelector } from '../../components/TableSelector';
import { TablesProcessingStatus } from '../../components/TablesProcessingStatus';
import { TablesSectionCard } from '../../components/TablesSectionCard';
import { useTablesSection } from '../../hooks/useTablesSection';
import type { TablesSectionProps } from '../../types';

export const TablesSection = ({ isLoading = false }: TablesSectionProps) => {
  const {
    hasTables,
    emptyTitle,
    emptyBody,
    options,
    selectedId,
    onSelect,
    isLoading: showSkeleton,
    selectedTable,
    selectedDefinitionTable,
    selectedTableNumber,
    isAnalyticalDatasetSelected,
    selectedDatasetSources,
    sources,
    projectId,
    variablesControls,
    isVariablePickerOpen,
    onCloseVariablePicker,
    analyticalDatasetProfileId,
    hasAnalyticalDatasetResults,
    datasetSourcesMissingFromRun,
    datasetView,
    onSelectDatasetView,
    listViewLabel,
    tableViewLabel,
    datasetEmptyHint,
    processingStatusLabel,
  } = useTablesSection(isLoading);

  // Both the real results grid and its pre-processed empty placeholder are
  // DataGrid-shaped and want the same fixed-height card.
  const isDatasetTableView = isAnalyticalDatasetSelected && datasetView === 'table';

  return !showSkeleton && !hasTables ? (
    <EmptyStateCard title={emptyTitle} body={emptyBody} data-testid="analysis-tables-empty" />
  ) : (
    <TablesSectionCard fillHeight={isDatasetTableView}>
      {showSkeleton ? (
        <Skeleton
          variant="rounded"
          height={40}
          width={320}
          animation="wave"
          sx={{ borderRadius: `${borderRadius.md}px` }}
          data-testid="analysis-tables-loading"
        />
      ) : (
        <>
          {processingStatusLabel && <TablesProcessingStatus label={processingStatusLabel} />}
          {/* One row: the table selector with its stepper, and for the Analytical
              dataset option its title and controls, so neither takes a line of its own. */}
          <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={4} rowGap={2}>
            <TableSelector options={options} selectedId={selectedId} onChange={onSelect} />
            {isAnalyticalDatasetSelected && (
              <AnalyticalDatasetHeader
                controls={variablesControls}
                view={datasetView}
                onSelectView={onSelectDatasetView}
                listViewLabel={listViewLabel}
                tableViewLabel={tableViewLabel}
              />
            )}
          </Stack>
          <TableDataPreview
            selectedTable={selectedTable}
            selectedDefinitionTable={selectedDefinitionTable}
            selectedTableNumber={selectedTableNumber}
            isAnalyticalDatasetSelected={isAnalyticalDatasetSelected}
            selectedDatasetSources={selectedDatasetSources}
            sources={sources}
            projectId={projectId}
            variablesTitle={variablesControls.title}
            isVariablePickerOpen={isVariablePickerOpen}
            onCloseVariablePicker={onCloseVariablePicker}
            analyticalDatasetProfileId={analyticalDatasetProfileId}
            hasAnalyticalDatasetResults={hasAnalyticalDatasetResults}
            datasetSourcesMissingFromRun={datasetSourcesMissingFromRun}
            datasetView={datasetView}
            datasetEmptyHint={datasetEmptyHint}
          />
        </>
      )}
    </TablesSectionCard>
  );
};
